import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { useMutation } from '@apollo/react-hooks'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import type { DialogProps } from '@paintscout/ui'
import {
  SearchBar,
  FilePreview,
  Tabs,
  Tab,
  Grid,
  Spinner,
  useDebounce,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@paintscout/ui'
import { gql } from 'apollo-boost'
import type { QuoteFile, QuoteFileVisibility, QuoteSource } from 'paintscout'
import React, { useState, useEffect } from 'react'
import values from 'lodash/values'
import omit from 'lodash/omit'
import FilesGrid from '../../FilesGrid'

export interface EditIntegrationItemFilesProps extends DialogProps {
  /**
   * QuoteFiles to include in dialog
   */
  files?: QuoteFile[]

  /**
   * Integration provider that has been requested (not used in the case of 'device')
   */
  provider?: string

  source?: QuoteSource

  /**
   * Automatically starts uploading these files when dialog mounts
   */
  uploadFiles?: File[]

  /**
   * QuoteFiles index to open at
   */
  index?: number

  onConfirm?: (files: QuoteFile[], deletedFiles?: QuoteFile[], source?: QuoteSource) => void
  onCancel?: () => void
}

const useStyles = makeStyles({
  root: {},
  fullHeight: {
    height: '100%'
  },
  content: {
    padding: 16,
    height: '100%'
  },
  stepper: {
    background: 'none'
  },
  paginationActions: {
    padding: 0,
    height: 'initial'
  },
  paginationActionsRightButton: {
    display: 'block'
  },
  searchBarRoot: {
    marginTop: 0
  },
  searchBarPaper: {
    padding: `8px 16px`
  },
  tabs: {
    marginBottom: 8
  }
})

export const PROCESS_EVENT = gql`
  mutation ($type: String!, $provider: String!, $async: Boolean, $params: JSON) {
    processEvent(type: $type, provider: $provider, async: $async, params: $params) {
      provider
      result
      error
    }
  }
`

function EditIntegrationItemFilesDialog(props: EditIntegrationItemFilesProps) {
  const {
    provider,
    uploadFiles: _uploadFiles,
    files: _files,
    index: _index,
    onConfirm,
    onCancel,
    ...baseDialogProps
  } = props

  const [selectedProject, setSelectedProject] = useState<QuoteSource>(props.source)
  const [query, setQuery] = useState(props.source?.name ?? props?.source?.id ?? '')
  const debouncedQuery = useDebounce(query, 250)
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [_error, setError] = useState(null)
  const [quoteFiles, setQuoteFiles] = useState([])
  const [annotatedFiles, setAnnotatedFiles] = useState([])
  const [projectResult, setProjectResult] = useState([])
  const [selectedFiles, setSelectedFiles] = useState({})
  const [selectedAnnotatedFiles, setSelectedAnnotatedFiles] = useState({})
  const [currentTab, setCurrentTab] = useState('annotated')
  const [currentPage, setCurrentPage] = useState(1)
  const [moreResults, setMoreResults] = useState(false)

  const classes = useStyles({})

  const [processEventMutation] = useMutation<any>(PROCESS_EVENT)

  const searchProjects = async () => {
    if (selectedProject) {
      return
    }

    setLoadingProjects(true)
    const { data: projectData } = await processEventMutation({
      variables: {
        type: 'get-projects',
        provider,
        async: false,
        params: {
          query
        }
      }
    })
    setLoadingProjects(false)
    setProjectResult(projectData.processEvent?.result?.result ?? [])
    if (projectData.processEvent?.result?.error) {
      setError(projectData.processEvent?.result?.error)
    }
  }

  const searchFiles = async ({ page }: { page: number }) => {
    if (!selectedProject) {
      return null
    }

    if (page === 1) {
      setLoadingFiles(true)
    }
    const { data: fileData } = await processEventMutation({
      variables: {
        type: 'get-files',
        provider,
        async: false,
        params: {
          projectId: selectedProject?.id,
          page
        }
      }
    })
    if (page === 1) {
      setLoadingFiles(false)
    }
    if (fileData.processEvent?.result?.error) {
      setError(fileData.processEvent?.result?.error)
      return
    }

    const fileResult = fileData.processEvent?.result?.result ?? []
    if (fileResult.length === 100) {
      setMoreResults(true)
    } else {
      setMoreResults(false)
    }

    const initialQuoteFiles = fileResult.map((photo) => {
      const key = `${photo.id}-original`
      // const isSelected = find(files, { key })
      const file = {
        key,
        visibility: 'visible',
        type: 'image',
        src: photo.url,
        caption: photo?.description ?? ''
        // selected: isSelected
      }
      // if (isSelected) {
      //   initialSelectedFiles[file.key] = file
      // }
      return file
    })
    setQuoteFiles((quoteFiles) => [...quoteFiles, ...initialQuoteFiles])

    // const initialSelectedAnnotatedFiles = {}
    const initialAnnotatedQuoteFiles = fileResult.map((photo) => {
      const key = `${photo.id}-annotated`
      // const isSelected = find(files, { key })
      const file = {
        key,
        visibility: 'visible',
        type: 'image',
        src: photo.annotation_url,
        caption: photo?.description ?? ''
        // selected: isSelected
      }
      // if (isSelected) {
      // initialSelectedAnnotatedFiles[file.key] = file
      // }
      return file
    })
    setAnnotatedFiles((annotatedFiles) => [...annotatedFiles, ...initialAnnotatedQuoteFiles])
    // we're going to forego this for now... i'm not sure how removing should work.
    // but hopefully if we don't select stuff, people will realize it's just a "add once"
    // type of interface, and they have to delete from elsewhere
    // setSelectedFiles(initialSelectedFiles)
    // setSelectedAnnotatedFiles(initialSelectedAnnotatedFiles)
  }

  const handleLoadMore = async () => {
    const newCurrentPage = currentPage + 1
    setCurrentPage(newCurrentPage)
    await searchFiles({ page: newCurrentPage })
  }

  useEffect(() => {
    searchProjects()
  }, [selectedProject, debouncedQuery])

  useEffect(() => {
    searchFiles({ page: currentPage })
  }, [selectedProject])

  const endAdornment = query ? (
    <Button
      variant="text"
      onClick={() => {
        setSelectedProject(null)
        setQuery('')
        setProjectResult([])
        setQuoteFiles([])
        setSelectedFiles([])
        setCurrentPage(1)
        setMoreResults(false)
      }}
    >
      Clear
    </Button>
  ) : null

  return (
    <Dialog {...baseDialogProps} maxWidth="md" fullWidth={true}>
      <DialogTitle>Add Media</DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <SearchBar
          disabled={!!selectedProject}
          placeholder="Search Projects..."
          classes={{ paper: classes.searchBarPaper, root: classes.searchBarRoot }}
          onChange={setQuery}
          value={query}
          endAdornment={endAdornment}
        />
        {!selectedProject && (
          <ProjectListResults
            loading={loadingProjects}
            projects={projectResult}
            onItemClick={(project, _index) => {
              const projectName = project?.name ?? project.id
              setSelectedProject({
                provider,
                name: projectName,
                id: project.id
              })
              setQuery(projectName)
              setQuoteFiles([])
              setAnnotatedFiles([])
              setSelectedFiles([])
              setCurrentPage(1)
              setMoreResults(false)
            }}
          />
        )}
        {selectedProject && (
          <div>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Tabs
                  variant="scrollable"
                  value={currentTab}
                  classes={{ root: classes.tabs }}
                  onChange={handleTabChange}
                >
                  <Tab value={'annotated'} label="Annotated" />
                  <Tab value={'original'} label="Original" />
                </Tabs>
              </Grid>
              <Grid item>
                <Button variant="text" size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
              </Grid>
            </Grid>

            {currentTab === 'original' && (
              <FilesGrid
                files={quoteFiles}
                onFileClick={toggleFile('original')}
                isLoading={loadingFiles}
                onLoadMore={moreResults ? handleLoadMore : null}
              />
            )}
            {currentTab === 'annotated' && (
              <FilesGrid
                files={annotatedFiles}
                onFileClick={toggleFile('annotated')}
                isLoading={loadingFiles}
                onLoadMore={moreResults ? handleLoadMore : null}
              />
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      >
        <Button
          type="submit"
          variant={'contained'}
          onClick={handleDialogConfirm}
          disabled={false}
          loading={loadingProjects || loadingFiles}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )

  function handleTabChange(event, value) {
    setCurrentTab(value)
  }

  function handleSelectAll(_event) {
    if (currentTab === 'original') {
      setSelectedFiles(
        [...quoteFiles].map((file) => {
          file.selected = true
          return file
        })
      )
    } else {
      setSelectedAnnotatedFiles(
        [...annotatedFiles].map((file) => {
          file.selected = true
          return file
        })
      )
    }
  }

  function toggleFile(type: string) {
    return function (index: number) {
      const file = type === 'original' ? { ...quoteFiles[index] } : { ...annotatedFiles[index] }
      if (!file) {
        return null
      }

      if (type === 'original') {
        quoteFiles[index].selected = !quoteFiles[index].selected
        setQuoteFiles(quoteFiles)

        if (selectedFiles[file.key]) {
          setSelectedFiles(omit(selectedFiles, [file.key]))
        } else {
          setSelectedFiles({ ...selectedFiles, [file.key]: file })
        }
      } else {
        annotatedFiles[index].selected = !annotatedFiles[index].selected
        setAnnotatedFiles(annotatedFiles)

        if (selectedAnnotatedFiles[file.key]) {
          setSelectedAnnotatedFiles(omit(selectedAnnotatedFiles, [file.key]))
        } else {
          setSelectedAnnotatedFiles({ ...selectedAnnotatedFiles, [file.key]: file })
        }
      }
    }
  }

  function handleDialogConfirm(_event: any) {
    if (onConfirm) {
      const filesValues = [...values(selectedFiles), ...values(selectedAnnotatedFiles)]
      const updatedFiles = filesValues.map((file: QuoteFile) => {
        const updatedFile = { ...file }
        delete updatedFile.selected
        return updatedFile
      }) as QuoteFile[]

      // No quote source sync for cc
      // const updatedProject = {
      // ...selectedProject
      // }
      // let sync = false
      // updatedFiles.forEach((file) => {
      // if (file.src.includes('companycam.com')) {
      // sync = true
      // }
      // })
      // if (sync) {
      // updatedProject.sync = true
      // }
      onConfirm(updatedFiles, null, selectedProject)
    }
  }
}

const useListItemStyles = makeStyles((theme: Theme) => ({
  listItem: {
    // padding: 8,
    display: 'flex',
    justifyContent: 'space-between'
  },
  listItemText: {
    flex: 'none'
  },
  secondaryText: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    fontStyle: 'italic'
  },

  featuredImageWrapper: {
    width: 60,
    '& img': {
      objectFit: 'contain'
    }
  }
}))

function ProjectListResults({ projects, loading, onItemClick }) {
  const classes = useListItemStyles()
  /*
    project: {
      id,
      address
        street_address_1
        street_address_2
        city
        state
        postal_code
        country
      name 
    }
  */

  if (loading) {
    return <Spinner padded />
  }

  return (
    <div>
      {projects.map((project, index) => {
        const address = project.address?.street_address_1 ?? ''

        return (
          <ListItem
            className={classes.listItem}
            divider
            button
            key={project.id}
            onClick={() => {
              onItemClick(project, index)
            }}
          >
            <ListItemText
              classes={{ root: classes.listItemText, secondary: classes.secondaryText }}
              primary={project.name}
              secondary={address}
              primaryTypographyProps={{
                variant: 'body1'
              }}
              secondaryTypographyProps={{
                variant: 'body1'
              }}
            />
            <div className={classes.featuredImageWrapper}>
              {project.featuredImage && (
                <FilePreview file={{ src: project.featuredImage, visibility: 'visible' as QuoteFileVisibility }} />
              )}
            </div>
          </ListItem>
        )
      })}
    </div>
  )
}

export default EditIntegrationItemFilesDialog
